import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import PriceTicker from '../components/PriceTicker'
import ProductCard from '../components/ProductCard'
import { Loader } from '../components/Loader'
import { productsApi } from '../services/api'
import { Product, ProductCategory, CATEGORY_LABELS } from '../types'

const CATEGORY_ITEMS: Array<{ value: ProductCategory; icon: string; desc: string; bg: string }> = [
  { value: 'SHMESH', icon: '🟨', desc: 'شمش طلا و نقره', bg: 'from-gold-50 to-gold-100 border-gold-200' },
  { value: 'ABSHADE', icon: '🫕', desc: 'طلا و نقره آب‌شده', bg: 'from-orange-50 to-orange-100 border-orange-200' },
  { value: 'COIN', icon: '🪙', desc: 'سکه‌های طلا و نقره', bg: 'from-amber-50 to-amber-100 border-amber-200' },
  { value: 'BAR', icon: '📦', desc: 'بار طلا و نقره', bg: 'from-yellow-50 to-yellow-100 border-yellow-200' }
]

const Home: React.FC = () => {
  const [featured, setFeatured] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    productsApi
      .getAll({ limit: 8, sortBy: 'sortOrder', sortOrder: 'asc' })
      .then((res) => {
        if (res.data.success && res.data.data) setFeatured(res.data.data.products)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      {/* Hero — فلز مذاب */}
      <section className="hero-molten text-white">
        <div className="container-main py-16 md:py-24 text-center">
          <span className="inline-block border border-gold-300/40 text-gold-200 rounded-full px-4 py-1 text-sm mb-5 tracking-tight">
            خرید و فروش آنلاین شمش، آب‌شده، سکه و بار
          </span>
          <h1 className="text-3xl md:text-5xl font-black leading-tight mb-4 tracking-tight">
            قیمت‌گذاری شفاف بر اساس{' '}
            <span className="text-gold-300">قیمت خام</span> جهانی
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto leading-7 mb-9">
            قیمت خام طلا و نقره به‌صورت خودکار از منابع جهانی دریافت می‌شود و درصد سود مجموعه به‌صورت شفاف روی آن اعمال می‌گردد.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/products" className="btn bg-gold-300 !text-stone-900 hover:!bg-gold-200 shadow-lg shadow-gold-500/20 !py-3 !px-8 font-black">
              مشاهده محصولات
            </Link>
            <Link to="/register" className="btn border-2 border-white/30 text-white hover:bg-white/10 !py-3 !px-8">
              ساخت حساب کاربری
            </Link>
          </div>
        </div>
      </section>

      <div className="container-main -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl shadow-stone-900/10 ring-1 ring-gold-300/40 p-5">
          <PriceTicker />
        </div>
      </div>

      {/* Categories */}
      <section className="container-main mt-12">
        <h2 className="text-xl font-black text-stone-900 mb-1 gold-vein inline-block px-1">دسته‌بندی محصولات</h2>
        <p className="text-sm text-gray-500 mb-6">شمش، آب‌شده، سکه و بار را بخرید یا به ما بفروشید</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORY_ITEMS.map((cat) => (
            <Link
              key={cat.value}
              to={`/products?category=${cat.value}`}
              className={`bg-gradient-to-b rounded-xl border p-5 text-center hover:shadow-md transition-shadow ${cat.bg}`}
            >
              <div className="text-4xl mb-2">{cat.icon}</div>
              <div className="font-bold text-gray-900">{CATEGORY_LABELS[cat.value]}</div>
              <div className="text-xs text-gray-600 mt-1">{cat.desc}</div>
            </Link>
          ))}
        </div>
      </section>
{/* Featured products */}
      <section className="container-main mt-14">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-black text-gray-900">محصولات منتخب</h2>
            <p className="text-sm text-gray-500 mt-1">آخرین قیمت‌ها بر اساس قیمت خام لحظه‌ای</p>
          </div>
          <Link to="/products" className="btn btn-outline !py-2">همه محصولات</Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* How it works */}
      <section className="container-main mt-16">
        <h2 className="text-xl font-black text-gray-900 text-center mb-8">نحوه قیمت‌گذاری</h2>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { n: '۱', t: 'قیمت خام', d: 'قیمت جهانی طلا و نقره به‌صورت خودکار دریافت و به هر گرم تبدیل می‌شود.' },
            { n: '۲', t: 'درصد سود مدیر', d: 'مجموعه، درصد سود شفافی برای هر محصول (شمش، آب‌شده و …) اعلام می‌کند.' },
            { n: '۳', t: 'قیمت فروش', d: 'قیمت نهایی فروش = قیمت خام × (۱ + درصد سود).' },
            { n: '۴', t: 'خرید از شما', d: 'اگر قصد فروش دارید، طبق قیمت خرید مجموعه با کسر سود اعلام‌شده از شما خرید می‌شود.' }
          ].map((step) => (
            <div key={step.n} className="card p-5 text-center">
              <div className="w-10 h-10 mx-auto bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-black mb-3">
                {step.n}
              </div>
              <h3 className="font-bold text-gray-900 mb-1">{step.t}</h3>
              <p className="text-xs text-gray-500 leading-5">{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-main mt-16">
        <div className="bg-gradient-to-l from-primary-600 to-gold-500 rounded-2xl p-8 md:p-12 text-white text-center">
          <h2 className="text-2xl font-black mb-2">طلای خود را به ما بفروشید</h2>
          <p className="text-white/90 mb-6">قیمت خرید شفاف، نقدی و فوری — ثبت سفارش فروش آنلاین</p>
          <Link to="/products" className="btn bg-white !text-primary-700 !py-3 !px-10 shadow-lg">شروع فروش</Link>
        </div>
      </section>
    </div>
  )
}

export default Home